// v2.18.2 — Page historique livreur PRO : stats agrégées + graph km/jour 30d +
// top destinations + liste paginée des courses avec mini-map SVG polyline.

import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function fmtDuration(min) {
  if (!Number.isFinite(min) || min <= 0) return "0 min";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m}min` : `${m} min`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
}

// Polyline SVG sur fond gris à partir des waypoints.
function MiniMap({ waypoints }) {
  const W = 80;
  const H = 80;
  if (!Array.isArray(waypoints) || waypoints.length < 2) {
    return (
      <div data-testid="course-minimap-empty" style={{ width: W, height: H, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#64748B", textAlign: "center", padding: 4 }}>
        Pas de trajet
      </div>
    );
  }
  const lats = waypoints.map((p) => p.lat);
  const lngs = waypoints.map((p) => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const dLat = Math.max(0.0001, maxLat - minLat);
  const dLng = Math.max(0.0001, maxLng - minLng);
  const pad = 6;
  const points = waypoints.map((p) => {
    const x = pad + ((p.lng - minLng) / dLng) * (W - 2 * pad);
    const y = pad + (1 - (p.lat - minLat) / dLat) * (H - 2 * pad); // flip lat
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const start = points.split(" ")[0].split(",");
  const end = points.split(" ").slice(-1)[0].split(",");
  return (
    <svg data-testid="course-minimap-svg" width={W} height={H} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6 }}>
      <polyline points={points} fill="none" stroke="#FBBF24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={start[0]} cy={start[1]} r="2.6" fill="#22C55E" stroke="#0F172A" strokeWidth="0.5" />
      <circle cx={end[0]} cy={end[1]} r="2.6" fill="#EF4444" stroke="#0F172A" strokeWidth="0.5" />
    </svg>
  );
}

function StatCard({ title, courses, km, durationMin, accent = "#F59E0B" }) {
  return (
    <div style={{
      flex: 1, minWidth: 130, padding: 14,
      background: "rgba(15, 23, 42, 0.7)",
      border: `1px solid ${accent}33`,
      borderRadius: 12,
      backdropFilter: "blur(12px)",
      boxShadow: `0 8px 22px rgba(0,0,0,0.35)`,
    }}>
      <div style={{ fontSize: 11, color: "#94A3B8", letterSpacing: "0.08em", textTransform: "uppercase" }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#F8FAFC", marginTop: 6 }}>{courses}</div>
      <div style={{ fontSize: 10, color: accent, marginTop: 2 }}>courses</div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 12, color: "#CBD5E1" }}>
        <span>📏 {Number(km).toFixed(1)} km</span>
        <span>⏱ {fmtDuration(durationMin)}</span>
      </div>
    </div>
  );
}

function CourseCard({ course, expanded, onToggle }) {
  const statusColor = course.status === "completed" ? "#22C55E" : course.status === "cancelled" ? "#94A3B8" : "#FBBF24";
  return (
    <div data-testid={`course-card-${course.course_id}`} onClick={onToggle} style={{
      padding: 12,
      background: "rgba(15, 23, 42, 0.65)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 10,
      cursor: "pointer",
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
    }}>
      <MiniMap waypoints={course.waypoints} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong style={{ fontSize: 13, color: "#F1F5F9" }}>{fmtDate(course.started_at)}</strong>
          <span style={{ fontSize: 10, fontWeight: 700, color: statusColor, padding: "2px 8px", borderRadius: 999, border: `1px solid ${statusColor}55`, background: `${statusColor}22` }}>
            {course.status}
          </span>
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: "#CBD5E1", display: "flex", gap: 12, flexWrap: "wrap" }}>
          <span>📏 <strong>{Number(course.distance_km_traveled || 0).toFixed(2)}</strong> km</span>
          <span>⏱ {fmtDuration(course.duration_minutes)}</span>
          <span style={{ color: "#94A3B8" }}>📍 {course.destination?.label || "—"}</span>
        </div>
        {expanded && (
          <div style={{ marginTop: 8, fontSize: 11, color: "#94A3B8", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 8 }}>
            <div>Origin : {course.origin?.label || `${course.origin?.lat?.toFixed(4)}, ${course.origin?.lng?.toFixed(4)}`}</div>
            {course.destination?.code && <div>Code : <code style={{ color: "#93C5FD" }}>{course.destination.code}</code></div>}
            <div>Waypoints : {course.waypoints?.length || 0}</div>
            <div style={{ color: "#475569", marginTop: 2 }}>id : {course.course_id}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CourierHistoriquePage() {
  const [courier, setCourier] = useState(null);
  const [status, setStatus] = useState("checking");
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [coursesTotal, setCoursesTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // v2.18.3 — Exports
  const [exportMonth, setExportMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  // Auth + initial load.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/v3/courier/me`, { withCredentials: true });
        if (!r.data?.ok) throw new Error("no_courier");
        setCourier(r.data.courier);
        setStatus("authorized");
      } catch (_) {
        setStatus("denied");
      }
    })();
  }, []);

  useEffect(() => {
    if (status === "denied") {
      window.location.replace("/livreur/login?next=%2Flivreur%2Fhistorique");
    }
  }, [status]);

  useEffect(() => {
    if (status !== "authorized") return;
    (async () => {
      try {
        const [s, c] = await Promise.all([
          axios.get(`${API}/v3/courier/stats`, { withCredentials: true }),
          axios.get(`${API}/v3/courier/courses?limit=10&offset=0`, { withCredentials: true }),
        ]);
        if (s.data?.ok) setStats(s.data);
        if (c.data?.ok) {
          setCourses(c.data.courses || []);
          setCoursesTotal(c.data.total || 0);
          setOffset((c.data.courses || []).length);
        }
      } catch (_) { /* noop */ }
    })();
  }, [status]);

  const loadMore = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/v3/courier/courses?limit=10&offset=${offset}`, { withCredentials: true });
      if (r.data?.ok) {
        const more = r.data.courses || [];
        setCourses((prev) => [...prev, ...more]);
        setOffset(offset + more.length);
      }
    } finally { setLoading(false); }
  };

  const onLogout = async () => {
    try { await axios.post(`${API}/v3/courier/logout`, {}, { withCredentials: true }); } catch (_) {}
    window.location.replace("/livreur/login");
  };

  const chartData = useMemo(() => {
    if (!stats?.daily_km_last_30d) return [];
    return stats.daily_km_last_30d.map((d) => ({
      date: d.date.slice(5), // "MM-DD" pour l'axe
      km: d.km,
      courses: d.courses,
    }));
  }, [stats]);

  if (status === "checking") {
    return <div style={pageStyles.bg}><div style={{ color: "#94A3B8" }}>Chargement…</div></div>;
  }
  if (status !== "authorized") {
    return <div style={pageStyles.bg}><div style={{ color: "#94A3B8" }}>Redirection…</div></div>;
  }

  return (
    <div data-testid="courier-historique-page" style={pageStyles.bg}>
      <header style={pageStyles.header}>
        <a href="/livreur/navigate" data-testid="back-to-map-btn" style={pageStyles.backBtn}>← Carte</a>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#94A3B8", letterSpacing: "0.1em" }}>HISTORIQUE PRO</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#F8FAFC" }}>
            {courier?.courier_name} · <span style={{ color: "#94A3B8" }}>{courier?.company_name}</span>
          </div>
        </div>
        <a href="/livreur/zones-chaudes" data-testid="heatmap-link-header" style={pageStyles.heatmapBtnHeader} title="Zones chaudes">🔥</a>
        <button type="button" data-testid="logout-btn" onClick={onLogout} style={pageStyles.logoutBtn}>⎋</button>
      </header>

      <main style={{ padding: "16px 14px 60px", maxWidth: 960, margin: "0 auto" }}>
        {/* Section 1 — Stats cards */}
        <section data-testid="stats-cards" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 22 }}>
          <StatCard title="Aujourd'hui" courses={stats?.today?.courses ?? 0} km={stats?.today?.distance_km ?? 0} durationMin={stats?.today?.duration_minutes ?? 0} accent="#FBBF24" />
          <StatCard title="7 derniers jours" courses={stats?.week?.courses ?? 0} km={stats?.week?.distance_km ?? 0} durationMin={stats?.week?.duration_minutes ?? 0} accent="#22C55E" />
          <StatCard title="30 derniers jours" courses={stats?.month?.courses ?? 0} km={stats?.month?.distance_km ?? 0} durationMin={stats?.month?.duration_minutes ?? 0} accent="#3B82F6" />
        </section>

        {/* Section 2 — Graphe */}
        <section data-testid="km-chart" style={pageStyles.panel}>
          <div style={pageStyles.panelTitle}>📈 Km par jour — 30 derniers jours</div>
          <div style={{ height: 200, marginTop: 8 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 9 }} interval={4} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 9 }} />
                <Tooltip
                  contentStyle={{ background: "#0F172A", border: "1px solid rgba(255,255,255,0.15)", fontSize: 11 }}
                  labelStyle={{ color: "#FBBF24" }}
                  itemStyle={{ color: "#F1F5F9" }}
                  formatter={(v, name) => name === "km" ? [`${v} km`, "Distance"] : [v, "Courses"]}
                />
                <Line type="monotone" dataKey="km" stroke="#FBBF24" strokeWidth={2} dot={{ r: 2, fill: "#FBBF24" }} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Section 3 — Top 5 destinations */}
        <section data-testid="top-destinations" style={pageStyles.panel}>
          <div style={pageStyles.panelTitle}>🏆 Top 5 destinations</div>
          {(stats?.top_destinations || []).length === 0 && (
            <div style={{ fontSize: 12, color: "#64748B", padding: "12px 0" }}>Pas encore assez de courses terminées.</div>
          )}
          <ul style={{ listStyle: "none", padding: 0, margin: 0, marginTop: 8 }}>
            {(stats?.top_destinations || []).map((d, i) => (
              <li key={d.label} data-testid={`top-dest-${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 11, color: "#FBBF24", width: 18, textAlign: "center" }}>#{i + 1}</span>
                  <strong style={{ fontSize: 13, color: "#F1F5F9" }}>{d.label}</strong>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 10, color: "#94A3B8" }}>{fmtDate(d.last_at)}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "rgba(251,191,36,0.15)", color: "#FBBF24", border: "1px solid rgba(251,191,36,0.35)" }}>×{d.count}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section 4 — Liste paginée */}
        <section data-testid="exports-section" style={pageStyles.panel}>
          <div style={pageStyles.panelTitle}>📥 Exporter mes rapports</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginTop: 10 }}>
            <input
              type="month"
              data-testid="export-month-input"
              value={exportMonth}
              onChange={(e) => setExportMonth(e.target.value)}
              style={{
                padding: "8px 10px", background: "rgba(15,23,42,0.95)", color: "#F1F5F9",
                border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12,
                fontFamily: "JetBrains Mono, Menlo, monospace",
              }}
            />
            <a
              href={`${API}/v3/courier/export/pdf?month=${exportMonth}`}
              data-testid="export-pdf-btn"
              style={{ ...pageStyles.exportBtn, background: "linear-gradient(135deg, #DC2626, #EF4444)", color: "#fff" }}
            >📄 Télécharger PDF</a>
            <a
              href={`${API}/v3/courier/export/csv?month=${exportMonth}`}
              data-testid="export-csv-btn"
              style={{ ...pageStyles.exportBtn, background: "linear-gradient(135deg, #16A34A, #22C55E)", color: "#0F172A" }}
            >📊 Télécharger CSV</a>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: "#94A3B8" }}>
            Les téléchargements utilisent ton cookie de session (aucune saisie nécessaire).
          </div>
        </section>

        <section data-testid="courses-list" style={{ marginTop: 22 }}>
          <div style={pageStyles.panelTitle}>🗂 Historique des courses ({coursesTotal})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {courses.length === 0 && (
              <div style={{ fontSize: 12, color: "#64748B", padding: 14, textAlign: "center", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 8 }}>
                Aucune course pour l'instant. Va sur <a href="/livreur/navigate" style={{ color: "#FBBF24" }}>la carte</a> pour en démarrer une.
              </div>
            )}
            {courses.map((c) => (
              <CourseCard
                key={c.course_id}
                course={c}
                expanded={expandedId === c.course_id}
                onToggle={() => setExpandedId(expandedId === c.course_id ? null : c.course_id)}
              />
            ))}
          </div>
          {courses.length < coursesTotal && (
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <button type="button" data-testid="load-more-btn" onClick={loadMore} disabled={loading} style={pageStyles.loadMoreBtn}>
                {loading ? "Chargement…" : "Charger plus"}
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

const pageStyles = {
  bg: {
    minHeight: "100vh",
    background: "radial-gradient(ellipse at top, #1E293B 0%, #0F172A 70%, #020617 100%)",
    color: "#E2E8F0",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: "rgba(15, 23, 42, 0.85)",
    borderBottom: "1px solid rgba(245, 158, 11, 0.25)",
    backdropFilter: "blur(10px)",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  backBtn: {
    color: "#CBD5E1",
    textDecoration: "none",
    fontSize: 12,
    padding: "6px 10px",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 999,
  },
  logoutBtn: {
    background: "rgba(239,68,68,0.12)",
    border: "1px solid rgba(239,68,68,0.4)",
    color: "#FCA5A5",
    fontSize: 13,
    padding: "5px 10px",
    borderRadius: 999,
    cursor: "pointer",
  },
  panel: {
    marginTop: 22,
    padding: 14,
    background: "rgba(15, 23, 42, 0.6)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    backdropFilter: "blur(12px)",
  },
  panelTitle: {
    fontSize: 12,
    color: "#FBBF24",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  loadMoreBtn: {
    background: "rgba(245, 158, 11, 0.15)",
    border: "1px solid rgba(245, 158, 11, 0.4)",
    color: "#FBBF24",
    padding: "8px 18px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  heatmapBtnHeader: {
    background: "rgba(239, 68, 68, 0.14)",
    border: "1px solid rgba(239, 68, 68, 0.45)",
    color: "#FCA5A5",
    fontSize: 13,
    padding: "5px 10px",
    borderRadius: 999,
    textDecoration: "none",
    cursor: "pointer",
    lineHeight: 1,
  },
  exportBtn: {
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
  },
};
